/* Reads a file chosen on the Admin screen into { columns: [heading, ...], rows: [[value, ...], ...] } (all values as text) for the server to load.
 * CSV (comma separated, quotes allowed, a leading byte-order mark ignored) and Excel .xlsx (first sheet, first row = headings). Blank rows are skipped. */

/** CSV text -> array of rows (arrays of strings). Handles "quoted, fields", "" for a quote inside quotes, and CR/LF/CRLF line ends. */
export function parseCsv(text) {
  const src = String(text).replace(/^﻿/, '');
  const rows = [];
  let row = [], field = '', quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"') { if (src[i + 1] === '"') { field += '"'; i++; } else quoted = false; } else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && src[i + 1] === '\n') i++; row.push(field); rows.push(row); row = []; field = ''; }
    else field += c;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows;
}

const tidy = (rows) => {
  const live = rows.filter((r) => r.some((v) => String(v ?? '').trim() !== ''));
  if (!live.length) throw new Error('The file is empty.');
  const [columns, ...data] = live;
  const width = (() => { let w = columns.length; while (w > 0 && String(columns[w - 1] ?? '').trim() === '') w--; return w; })();   // ignore trailing blank headings
  return { columns: columns.slice(0, width).map((h) => String(h ?? '').trim()), rows: data.map((r) => Array.from({ length: width }, (_, i) => (r[i] === undefined || r[i] === null ? '' : String(r[i])))) };
};

/** An Excel cell value -> text (dates as YYYY-MM-DD, formulas as their result, rich text as plain text). */
export function cellText(v) {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') {
    if ('result' in v) return cellText(v.result);
    if (Array.isArray(v.richText)) return v.richText.map((t) => t.text).join('');
    if ('text' in v) return String(v.text);
    if ('error' in v) return '';
  }
  return String(v);
}

export async function parseExcel(buffer) {
  const mod = await import('exceljs/dist/exceljs.min.js');
  const ExcelJS = mod.default ?? mod;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buffer);
  const ws = wb.worksheets[0];
  if (!ws) throw new Error('The workbook has no sheets.');
  const rows = [];
  ws.eachRow({ includeEmpty: false }, (row) => { const out = []; row.eachCell({ includeEmpty: true }, (cell, n) => { out[n - 1] = cellText(cell.value); }); rows.push(Array.from(out, (v) => v ?? '')); });
  return tidy(rows);
}

/** A File from the file picker -> { columns, rows }. */
export async function parseSourceFile(file) {
  const name = String(file.name || '').toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) return tidy(parseCsv(await file.text()));
  if (name.endsWith('.xlsx')) return parseExcel(await file.arrayBuffer());
  throw new Error('Choose a .csv or .xlsx file.');
}
